/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getGdnPaidInvoiceDetailsAsync, getInvoicePaymentSchedulesAsync } from './DataActionExtension.g';
import { IInvoicePaymentScheduleEntity, InvoicePaymentScheduleEntityExtensionClass } from './DataServiceEntities.g';

/**
 * GetInvoiceBreakdown Input Action
 */
export class GetInvoiceBreakdownInput implements Msdyn365.IActionInput {
    public invoiceIds: string[];

    constructor(invoiceIds: string[]) {
        this.invoiceIds = invoiceIds;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `INVOICEPAYMENTSCHEDULE|${this.invoiceIds.join('|')}`;
    public getCacheObjectType = () => 'INVOICEPAYMENTSCHEDULE';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetInvoiceBreakdownData {
    invoiceBreakdownList: IInvoicePaymentScheduleEntity[];
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetInvoiceBreakdownInput([]);
};

async function action(input: GetInvoiceBreakdownInput, ctx: Msdyn365.IActionContext): Promise<IGetInvoiceBreakdownData> {
    // get unpaid invoice info
    const response = await getInvoicePaymentSchedulesAsync({ callerContext: ctx }, input.invoiceIds);
    let allInvoices = response;
    // get paid invoice info
    const filteredInvoices = input.invoiceIds.filter(inv => !response.some(unpaid => unpaid.Invoice === inv));
    if (filteredInvoices.length > 0) {
        const invoiceIds = filteredInvoices.join(',');
        const paidInvoicesResp = await getGdnPaidInvoiceDetailsAsync({ callerContext: ctx }, invoiceIds);

        const paidInvoicesInfo = paidInvoicesResp.PaidInvoiceDetails?.map(inv => {
            // sales order number, terms, and po
            const object = {
                Invoice: inv.InvoiceDetails?.InvoiceNumber,
                DueDate: inv.InvoiceDetails?.DueDate,
                Description: inv.InvoiceDetails?.Terms,
                PurchaseOrder: inv.InvoiceDetails?.PoNumber,
                SalesId: inv.InvoiceDetails?.SalesOrderNumber
            };
            const invoicePaymentEntity = new InvoicePaymentScheduleEntityExtensionClass(object);
            return invoicePaymentEntity;
        });
        allInvoices = paidInvoicesInfo ? response.concat(paidInvoicesInfo) : response;
    }
    return { invoiceBreakdownList: allInvoices };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetInvoiceBreakdownData>>action,
    id: 'GetInvoiceBreakdown',
    input: createInput
});
